// Attachments in the browser (§13.3): the intent, the direct PUT to quarantine with exactly the
// signed headers, the finalize, and the download slot. The SHA-256 is computed here before the
// upload so the store can refuse a body that differs from what was declared.

import {
  ALLOWED_ATTACHMENT_TYPES,
  ATTACHMENT_MAX_BYTES,
  type Attachment,
  type AttachmentContentType,
  AttachmentDownloadSchema,
  AttachmentIntentResponseSchema,
  AttachmentListSchema,
  AttachmentSchema,
  type Me,
} from "@hoffman-support/contracts";
import { ApiError } from "./api";

async function parse<T>(res: Response, parser: (v: unknown) => T): Promise<T> {
  if (!res.ok) {
    let failure = { status: res.status, code: "unknown", message: res.statusText };
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string };
        issues?: Array<{ path: string; message: string }>;
      };
      failure = {
        ...failure,
        code: body.error?.code ?? "unknown",
        message: body.error?.message ?? res.statusText,
        ...(body.issues ? { issues: body.issues } : {}),
      };
    } catch {
      // Non-JSON failure body.
    }
    throw new ApiError(failure);
  }
  return parser(await res.json());
}

function base(staff: boolean, id: string) {
  return `${staff ? "/api/admin/requests" : "/api/requests"}/${encodeURIComponent(id)}/attachments`;
}

export function listAttachments(id: string, staff = false) {
  return fetch(base(staff, id), { credentials: "same-origin" }).then((r) =>
    parse(r, (v) => AttachmentListSchema.parse(v).items),
  );
}

export function downloadSlot(id: string, attachmentId: string, staff = false) {
  return fetch(`${base(staff, id)}/${encodeURIComponent(attachmentId)}/download`, {
    credentials: "same-origin",
  }).then((r) => parse(r, (v) => AttachmentDownloadSchema.parse(v)));
}

export function isAllowedType(type: string): type is AttachmentContentType {
  return (ALLOWED_ATTACHMENT_TYPES as readonly string[]).includes(type);
}

async function sha256Base64(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  let binary = "";
  for (const b of new Uint8Array(digest)) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Intent, upload, finalize. Throws an ApiError with a readable message at every refusal. */
export async function uploadAttachment(
  me: Me,
  id: string,
  file: File,
  staff = false,
): Promise<Attachment> {
  if (!isAllowedType(file.type)) {
    throw new ApiError({
      status: 400,
      code: "type_not_allowed",
      message: `${file.type || "that file type"} is not accepted; upload a PNG, JPEG, GIF, WebP, PDF, or plain-text file`,
    });
  }
  if (file.size > ATTACHMENT_MAX_BYTES) {
    throw new ApiError({
      status: 400,
      code: "too_large",
      message: `The file is larger than ${Math.round(ATTACHMENT_MAX_BYTES / 1024 / 1024)} MB`,
    });
  }
  const bytes = await file.arrayBuffer();
  const sha256 = await sha256Base64(bytes);
  const intent = await fetch(base(staff, id), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      "x-csrf-token": me.csrfToken,
      "idempotency-key": crypto.randomUUID(),
    },
    body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size, sha256 }),
  }).then((r) => parse(r, (v) => AttachmentIntentResponseSchema.parse(v)));

  const put = await fetch(intent.upload.url, {
    method: "PUT",
    headers: intent.upload.headers,
    body: bytes,
  });
  if (!put.ok) {
    throw new ApiError({
      status: put.status,
      code: "upload_failed",
      message: "The upload was refused by the storage service; try again",
    });
  }
  return fetch(`${base(staff, id)}/${encodeURIComponent(intent.id)}/finalize`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-csrf-token": me.csrfToken },
  }).then((r) => parse(r, (v) => AttachmentSchema.parse(v)));
}
